import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';
import { Stack } from '../../design-system/components/Layout';
import { spacing } from '../../design-system/tokens/spacing';
import { DashboardHeader } from '../../components/dashboard/DashboardHeader';
import { AdminNeedsAttention } from '../../components/dashboard/AdminNeedsAttention';
import { AdminSnapshotPanel } from '../../components/dashboard/AdminSnapshotPanel';
import { AdminActionGroups } from '../../components/dashboard/AdminActionGroups';
import { ExternalNewsWidget } from '../../components/dashboard/ExternalNewsWidget';
import { NotificationCard } from '../../components/dashboard/NotificationCard';
import { PrimaryNextAction } from '../../components/dashboard/PrimaryNextAction';
import { DashboardSkeleton } from '../../components/dashboard/DashboardSkeleton';
import { DashboardErrorState } from '../../components/dashboard/DashboardErrorState';
import { pickAdminPrimarySource } from '../../utils/dashboardPrimaryAction';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
  width: 100%;
  max-width: 1180px;
`;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { viewModel, loading, error, reload } = useAdminDashboard();

  const primarySource = useMemo(
    () =>
      viewModel
        ? pickAdminPrimarySource({
            urgentNotices: viewModel.urgentNotices,
            attentionItems: viewModel.attentionItems,
            totalPendingWork: viewModel.totalPendingWork,
          })
        : null,
    [viewModel]
  );

  const primaryAction = useMemo(() => {
    if (!primarySource) return null;
    if (primarySource.kind === 'notice' && primarySource.notice) {
      const n = primarySource.notice;
      return {
        title: n.title,
        message: n.message,
        actionLabel: n.actionLabel ?? t('common.continue'),
        actionLink: n.actionLink ?? '/',
        noticeIndex: primarySource.noticeIndex,
      };
    }
    if (primarySource.kind === 'attention' && primarySource.attention) {
      const a = primarySource.attention;
      return {
        title: a.title,
        message: t('admin.home.attention.pendingCount', {
          count: a.count,
          defaultValue: `${a.count} pending — review and take action.`,
        }),
        actionLabel: a.actionLabel,
        actionLink: a.link,
      };
    }
    return null;
  }, [primarySource, t]);

  if (loading && !viewModel) {
    return <DashboardSkeleton />;
  }

  if (error && !viewModel) {
    return <DashboardErrorState onRetry={reload} message={error} />;
  }

  if (!viewModel) {
    return <DashboardErrorState onRetry={reload} />;
  }

  const remainingNotices = viewModel.urgentNotices.filter(
    (_, index) => index !== primaryAction?.noticeIndex
  );

  return (
    <Page>
      <DashboardHeader greeting={viewModel.greeting} description={viewModel.description} />

      {primaryAction && (
        <PrimaryNextAction
          title={primaryAction.title}
          message={primaryAction.message ?? ''}
          actionLabel={primaryAction.actionLabel ?? t('common.continue')}
          onAction={() => navigate(primaryAction.actionLink)}
        />
      )}

      {remainingNotices.length > 0 && (
        <Stack direction="column" spacing="md">
          {remainingNotices.map((notice, index) => (
            <NotificationCard
              key={`${notice.title}-${index}`}
              type={notice.type}
              title={notice.title}
              message={notice.message}
              dismissible={false}
              action={
                notice.actionLabel && notice.actionLink
                  ? {
                      label: notice.actionLabel,
                      onClick: () => navigate(notice.actionLink!),
                    }
                  : undefined
              }
            />
          ))}
        </Stack>
      )}

      <AdminNeedsAttention
        items={viewModel.attentionItems}
        totalPendingWork={viewModel.totalPendingWork}
      />

      <AdminSnapshotPanel cells={viewModel.snapshot} />

      <AdminActionGroups
        primary={viewModel.primaryActions}
        secondary={viewModel.secondaryActions}
      />

      <ExternalNewsWidget allowRefresh />
    </Page>
  );
};

export default AdminDashboard;
